<template>
	<div class="draggable" :class="itemClass" draggable="false"
		 ref="el">
		<!--				<span class="caption">{{zone}} : {{acceptsZones}}</span>-->
		<slot name="default" :onMouseDown="onDragStart"></slot>
	</div>
</template>

<script>
import {debounce} from "lodash";
import {onlyUnique} from "@/utils/utils";

const DROPPING_TIME = 250;

const registeredComponents = new Map();

export default {
	name: "draggable",
	//mixins: [base],
	props: {
		/**
		 * Unique value that identifies item we drag.
		 * This should be unique within all possible dragged items.
		 */
		id: {
			type: String | Number | Object,
			default: null,
		},
		/**
		 * Unique object that represents an item we are dragging  - this is what is dragged with its handler
		 */
		item: {
			default: null
		},
		/**
		 * Array of sibling objects that may also be affected by dragging (eg, they are also selected) - this are items are dragged NOT by their handler
		 */
		siblings: {
			type: Array,
			default: null
		},
		preference: {
			type: Number,
			default: 0,
		},
		zone: {
			type: String
		},
		dropZones: {
			type: Array
		},
		acceptsZones: {
			type: Array
		},
		rejectsIds: {
			type: Array
		},
		inline: {
			type: Boolean,
			default: false
		},
		enabled: {
			type: Boolean,
			default: true
		},
		draggable: {
			type: Boolean,
			default: true
		},
		droppable: {
			type: Boolean,
			default: true,
		},
		move: {
			type: Boolean,
			default: false
		},
		dropProcessor: {
			type: Function,
		}
	},
	data: () => ({
		isDragging: false,
		dragStartMouseX: null,
		dragStartMouseY: null,
		mouseX: null,
		mouseY: null,
		mouseXOffset: null,
		mouseYOffset: null,

		dummy: null,
		canFollow: false,
		_posDummyDebounced: null,

		lastDroppable: null,
		isSmthOverMe: false,

		findDroppableRafId: null,
	}),
	computed: {
		itemClass() {
			const c = [];
			if (this.enabled) c.push("--draggable");
			if (this.droppable) c.push("droppable");
			if (this.isSmthOverMe) c.push("--over");
			if (this.inline) c.push("--inline");
			if (this.isDragging) c.push("--dragging");
			if (this.move && this.isDragging) c.push("--moved");
			//if (this.isDragged) c.push("--dragged");
			//else if (this.dragOverCounter) c.push("--dragover");
			//if (this.draggedElement && (!this.acceptsZones || this.acceptsZones?.includes(this.draggedElement.zone))) c.push("--drop-zone");
			//if ( this.isDropped ) c.push("--dropped");
			return c;
		},
		groupItems() {
			const items = [...(this.siblings || [])];

			// TODO this bugs - for new items (without refresh) crates infinite group

			if (!items.find(el => el === this.item || el.id === this.id)) items.push(this.item);
			return items;
		},
		groupComponents() {
			// перетаскиваемый элемент - это сам компонент: одну и ту же задачу могут рендерить сразу две строки
			// (например, цель процесса и сама строка процесса), поиск по item нашёл бы не ту
			return this.groupItems.map(item => item === this.item ? this : this.findComponentByItem(item)).filter(component => !!component);
		},
		x() {
			return this.mouseX + this.mouseXOffset;
		},
		y() {
			return this.mouseY + this.mouseYOffset;
		},
		element() {
			const payload = [...(this.siblings || []), this.item].filter(el => !!el).filter(onlyUnique);
			return {
				zone: this.zone,
				id: this.id,
				item: this.item,
				siblings: this.siblings,
				payload,
			}
		}
	},
	methods: {
		onDragStart(event) {
			/*console.log("this.enabled", this.enabled);
			console.log("this.draggable", this.draggable);
			console.log("this.item", this.item);
			console.log("this.isDragging", this.isDragging);*/

			if (!this.enabled) return;
			if (!this.draggable) return;
			if (typeof this.item == "undefined") return;
			if (this.isDragging) return;
			//if (event.button !== 0) return;

			//console.log("Drag starting?");
			this.dragStartMouseX = event.clientX;
			this.dragStartMouseY = event.clientY;

			document.addEventListener("mousemove", this.onMouseMove);
			document.addEventListener("touchmove", this.onMouseMove);
			document.addEventListener("mouseup", this.onMouseUp);
			document.addEventListener("touchend", this.onMouseUp);
		},
		onMouseMove(event) {
			const offset = 1;

			this.mouseX = event.clientX;//event.pageX;
			this.mouseY = event.clientY;

			// prevent catching minimal starting drag (in clicks)
			if (!this.isDragging && !(Math.abs(this.dragStartMouseX - this.mouseX) >= offset
				|| Math.abs(this.dragStartMouseY - this.mouseY) >= offset)) return;

			//event.preventDefault();

			if (this.isDragging) {
				this.onDrag();
				this.scheduleFindDroppable();
			} else {
				// start dragging

				//console.log("Drag started");
				this.setIsDraggingForGroup(true);
				this.setCanFollowForGroup(false);

				let coords = this.$refs.el.getBoundingClientRect();
				this.mouseXOffset = coords.left - this.mouseX;
				this.mouseYOffset = coords.top - this.mouseY;

				this.createGroupDummies();

				// pause to allow dummies to be created WITHOUT --dragged/--dragged-secondary
				setTimeout(() => {
					this.groupComponents.forEach(component => {
						const item = component.getItem();
						component.setDummyDragged(item !== this.item);
						if (item === this.item) {
							this.$emit("start", item);
							if (this.groupItems.length > 1) this.setDummyMultiple();
						} else {
							//setTimeout(() => {
							component.hideDummy();
							//}, INITING_TIME);
						}
						component.canFollow = true;
					});
				}, 50);
			}
		},
		onMouseUp(event) {
			event.preventDefault();
			event.stopPropagation();

			document.removeEventListener("mousemove", this.onMouseMove);
			document.removeEventListener("touchmove", this.onMouseMove);
			document.removeEventListener("mouseup", this.onMouseUp);
			document.removeEventListener("touchend", this.onMouseUp);

			if (!this.isDragging) return;

			// uncomment to debug dummy html
			/*console.log("Dropping!!!");
			return;*/

			this.cancelScheduledFindDroppable();

			this.mouseX = event.clientX;
			this.mouseY = event.clientY;
			this.posDummy();
			const droppable = this.findDroppable();
			this.onDragEnd(droppable);
		},
		onDrag() {
			this.groupComponents.forEach(component => {
				if (component.canFollow) {
					if (component.getItem() === this.item) {
						component.$emit("drag", component.getItem());
						component.posDummy(this.x, this.y);
					} else component.posDummy(this.x + 10, this.y + 10);

					if (typeof component.getItem()?.onDrag === "function") component.getItem().onDrag(this.item);
				}
			});
		},
		/**
		 * Smth is dropped over this droppable.
		 * @param element
		 * @param component
		 */
		onDrop(element, component) {
			//console.log("onDrop", this);
			this.$emit("drop", element, this.element);

			if (typeof this.dropProcessor == "function") return this.dropProcessor(element, this.element);
			return null;
		},
		/**
		 * Drag process is ended for this draggable.
		 * @param droppable
		 */
		onDragEnd(droppable) {
			//console.log("onDragEnd()", this.element, droppable?.zone);

			// uncomment this to debug dummy view
			//return;

			let dropResult;
			if (droppable) {
				// tell droppable smth is dropped over it
				dropResult = droppable.onDrop(this.element, this);
				droppable.setOver(false);
			}
			this.lastDroppable?.setOver(false);

			// define dropped group now as it may change while drop is still processed
			const droppedGroup = [...this.groupComponents];

			// this is called after drop is fully processed
			const afterDropIsProcessed = () => {
				this.deleteGroupDummies();

				// small pause to allow dummy drop animation
				setTimeout(() => {
					this.setIsDraggingForGroup(false, droppedGroup);
					//console.log("SET dragging=false after dropping time");

					// tell parent we are fully done
					this.$emit("end", this.element, droppable?.getElement());
				}, DROPPING_TIME);
			}

			//console.log("DROPPABLE", droppable?.zone, this.dropZones);
			if (droppable) {
				if (!this.dropZones || this.dropZones.includes(droppable.zone)) {
					// droppable is within allowed drop zones

					//this.setDummyDropping(droppable.$el);
					//console.log("Dropping!");

					const dropDummy = () => {
						// start drop animation
						this.groupComponents.forEach(component => component.setDummyReturning(true));
						afterDropIsProcessed();
					};

					// wait till.draggable position is updated after the earlier 'drop' emit - and move to it!
					if (dropResult && typeof dropResult.finally === 'function') {
						// drop result is a promise - wait till its processed
						console.log("onDrop is a promise");
						dropResult
							.then(() => {
							})
							.catch(() => {
							}).finally(() => {
							//console.log("finally");
							dropDummy();
						});

					} else {
						// emulated processing
						// wait till .draggable position is _hopefully_ updated after the earlier 'drop' emit - and move to it!
						console.log("onDrop is NOT a promise");
						setTimeout(() => {
							//this.groupComponents.filter(component => component !== this).forEach((component, index) => component.setDummyReturning());
							dropDummy();
						}, 100);
					}
				} else {
					console.log("unsupported drop zone: " + droppable.zone);

					// do not process - just drop to droppable rect
					/*this.groupComponents.forEach(component => component.setDummyDropping(droppable.$el));
					afterDropIsProcessed();*/
				}
			} else {
				console.log("droppable not found");
				// appropriate droppable not found under mouse pos
				// "возвращаем" к исходному DOM-элементу (если DOM-элемент двигался - это будет новая позиция)
				//console.log("Returning...");
				this.groupComponents.forEach(component => component.setDummyReturning());
				afterDropIsProcessed();
			}

			//console.log("onDragEnd!");
		},
		/**
		 * Returns a droppable of allowed zone under current mouse pos.
		 */
		findDroppable() {

			const isElementVisible = (el) => {
				if (!el) return false;

				//return el.checkVisibility({contentVisibilityAuto: true, opacityProperty: true, visibilityProperty: true});
				//return el.checkVisibility({contentVisibilityAuto: true});

				const rect = el.getBoundingClientRect();
				// DOMRect { x: 8, y: 8, width: 100, height: 100, top: 8, right: 108, bottom: 108, left: 8 }
				const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
				const windowWidth = (window.innerWidth || document.documentElement.clientWidth);

				// http://stackoverflow.com/questions/325933/determine-whether-two-date-ranges-overlap
				const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
				const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

				return (vertInView && horInView);
			}
			const isElementUnderCoords = (el, x, y) => {
				if (!el) return false;
				const rect = el.getBoundingClientRect();
				return rect.left <= x && rect.right >= x
					&& rect.top <= y && rect.bottom >= y;
			}

			/*const underlyingElements = [...document.querySelectorAll('.droppable:hover')];
			const droppable = registeredComponents
				.find(component => underlyingElements.find(el => el === component.$el)
					&& component._props.enabled
					&& (!component._props.acceptsZones?.length || component._props.acceptsZones?.includes(this.zone))
					&& !component.isDragging
				);*/
			/*const droppables = registeredComponents
				.filter(component => isElementVisible(component.$el))
				.filter(component => component._props.enabled)
				.filter(component => !component.isDragging)*/
			let droppables = [...registeredComponents.values()]
				.filter(component => isElementUnderCoords(component.$el, this.mouseX, this.mouseY)
					&& component._props.droppable
					&& component._props.enabled
					&& (!component._props.acceptsZones?.length || component._props.acceptsZones?.includes(this.zone))
					&& (!component._props.rejectsIds?.length || !this.id || !component._props.rejectsIds?.includes(this.id))
					&& !component.isDragging
				);
			let droppable;

			//console.log("droppables", droppables?.map(el=>el.id));
			//console.log("registeredComponents", registeredComponents?.map(el=>el.id));
			//const debugId = 'dummy-droppable-folder-'+'tpjHXazFRJqIsDOT9s74WyFl';
			/*const debugId = 'dummy-droppable-before-' + 'tpjHXazFRJqIsDOT9s74WyFl';
			const testComponent = registeredComponents?.find(el => el.id === debugId);
			if (testComponent) {
				console.log("registeredComponent:", testComponent);
				console.log("isElementUnderCoords", isElementUnderCoords(testComponent.$el, this.mouseX, this.mouseY));
				const testDroppable = droppables?.find(el => el.id === debugId);
				if (testDroppable) {
					console.log("droppable:", testDroppable);
				}
			}*/

			if (droppables?.length) {
				droppables = [...droppables];
				droppables.sort((a, b) => b.preference - a.preference);
				droppable = droppables[0];
			}

			//console.log("DROPPABLES", droppables?.map(el=>el._props.id));
			/*console.group("DROPPABLE", droppable?._props.id);
			console.log("zone", droppable?.zone);
			console.log("el", droppable?.$el);
			console.log("acceptsZones", droppable?._props.acceptsZones);
			//console.log("rejectsZones", droppable?._props.rejectsZones);
			console.log("rejectsIds", this.id, droppable?._props.rejectsIds);
			console.groupEnd();*/

			if (droppable) {
				if (this.lastDroppable) this.lastDroppable.setOver(false);
				droppable.setOver(true);
				this.lastDroppable = droppable;
			}

			return droppable;
		},
		/**
		 * Троттлинг findDroppable() до одного вызова на кадр анимации - без этого каждое
		 * событие mousemove/touchmove при перетаскивании вызывает getBoundingClientRect()
		 * (синхронный reflow) для всех зарегистрированных draggable-компонентов.
		 */
		scheduleFindDroppable() {
			if (this.findDroppableRafId) return;
			this.findDroppableRafId = requestAnimationFrame(() => {
				this.findDroppableRafId = null;
				this.findDroppable();
			});
		},
		cancelScheduledFindDroppable() {
			if (!this.findDroppableRafId) return;
			cancelAnimationFrame(this.findDroppableRafId);
			this.findDroppableRafId = null;
		},
		posSecondaryDummy(x, y, withDebounce = false) {
			this.posDummy(x + 10, y + 10);
			/*if (withDebounce) {
				if (!this._posDummyDebounced) {
					this._posDummyDebounced = debounce((x, y) => {
						this.posDummy(x, y);
					}, 50);
				}
				this._posDummyDebounced(x + 10, y + 10);
			} else this.posDummy(x, y);*/
		},
		posDummy(x, y) {
			if (!this.dummy) return;
			//console.log("Posing: "+this.mouseX+":"+this.mouseY);
			this.dummy.style.left = typeof x !== "undefined" ? x + "px" : this.x + "px";
			this.dummy.style.top = typeof y !== "undefined" ? y + "px" : this.y + "px";
			//console.log("DUMMY POSED", this.dummy);
		},
		hideDummy() {
			if (!this.dummy) return;
			this.dummy.classList.add("--hidden");
		},
		createDummy() {
			// init dragged element as pure DOM element
			/*this.dummy = this.$refs.content.cloneNode(true);
			this.dummy.style.width = this.$refs.content.offsetWidth + 'px';
			this.dummy.style.height = this.$refs.content.offsetHeight + 'px';
			//this.dummy.classList.remove("draggable__content");
			this.dummy.classList.add("draggable__dummy");

			// TODO: add taken--right && taken--left
			this.dummy.classList.add("--taken");
			document.body.appendChild(this.dummy);*/

			const el = this.$el;//this.$refs['el'];
			const rect = el.getBoundingClientRect();
			//const x = rect.left;
			this.dummy = document.createElement("div");
			document.body.appendChild(this.dummy);
			this.dummy.classList.add("draggable__dummy");
			this.dummy.innerHTML = el.innerHTML;
			this.dummy.style.width = rect.width + "px";
			this.dummy.style.height = rect.height + "px";
			this.dummy.style.left = rect.left + "px";
			this.dummy.style.top = rect.top + "px";
			//console.log("Dummy created", this.dummy);
			return rect;
		},
		deleteDummy() {
			if (!this.dummy) return;
			if (!this.dummy.classList.contains("--hidden")) this.dummy.classList.add("--hidden");
			// 2. no we wait until dummy is hidden
			setTimeout(() => {
				//console.log("REMOVING", this.dummy);
				if (this.dummy) {
					document.body.removeChild(this.dummy);
					//console.log("Dummy deleted", this.dummy);
					this.dummy = null;
				}
			}, DROPPING_TIME);
		},
		createGroupDummies() {
			this.groupComponents.forEach(component => component.createDummy());
		},
		deleteGroupDummies() {
			this.groupComponents.forEach(component => component.deleteDummy());
		},
		getId() {
			return this.id;
		},
		getItem() {
			return this.item;
		},
		getElement() {
			return this.element;
		},
		setOver(value) {
			this.isSmthOverMe = value;
			//console.log("OVER ME", this.id, value);
		},
		setDummyMultiple(value) {
			if (!this.dummy) return;
			this.dummy.classList.add("--multiple");
		},
		setDummyDragged(isSecondary = false) {
			if (!this.dummy) return;
			this.dummy.classList.add("--dragged");
			if (isSecondary) this.dummy.classList.add("--dragged-secondary");
			else if (this.groupItems?.length > 1) this.dummy.classList.add("--dragged-multiple");
			//console.log("SET DUMMY DRAGGED", this.item.id);
		},
		setDummyDropping(target) {
			if (!this.dummy) return;
			if (!target) return;
			this.dummy.classList.add("--dropping");
			this.dummy.classList.remove("--hidden");
			let rect = target.getBoundingClientRect();
			this.dummy.style.left = rect.left + "px";
			this.dummy.style.top = rect.top + "px";

			const dummyRect = this.dummy.getBoundingClientRect();
			const scaleX = rect.width / dummyRect.width;
			const scaleY = rect.height / dummyRect.height;
			const scale = Math.min(scaleX, scaleY);
			this.dummy.style.transform = "scale(1)";//"scale(" + scale + ")";
		},
		setDummyReturning(withLookup = false) {
			//console.log("this.dummy", this.dummy);
			if (!this.dummy) return;
			this.dummy.classList.add("--returning");
			this.dummy.classList.remove("--hidden");
			let rect;
			if (this.$refs.el && !withLookup) {
				// если еще существует наш исходных дом-элемент
				rect = this.$refs.el.getBoundingClientRect();

				//console.log("dummy is moving component by dom el");
			} else {
				// найти его по значению
				const component = this.findComponentByItem(this.item);
				if (component) {
					rect = component.$el.getBoundingClientRect();

					//console.log("dummy is moving component by item");
				}
			}
			if (rect) {
				this.dummy.style.left = rect.left + "px";
				this.dummy.style.top = rect.top + "px";

				//console.log("rect exists");
			}
		},
		setIsDragging(value) {
			//console.log("setIsDragging: "+value+" for "+this.id, this.$refs.el);
			this.isDragging = value;
		},
		setIsDraggingForGroup(value, groupComponents) {
			//console.log("setIsDraggingForGroup(" + value + ")");
			groupComponents = groupComponents || this.groupComponents;
			groupComponents?.forEach(component => component.setIsDragging(value));
		},
		setCanFollowForGroup(value) {
			this.groupComponents.forEach(component => component.canFollow = value);
		},
		findComponentByItem(item) {
			return [...registeredComponents.values()].find(component => component.getItem() === item);
		},
		/**
		 * Регистрируем только компоненты, которые вообще могут участвовать в drag&drop
		 * (перетаскиваемые или принимающие drop) - неактивные draggable (например, зоны
		 * drop, отключённые через withDND) не должны попадать в findDroppable().
		 */
		registerComponent() {
			if (!this.item && !this.id) return;
			if (!this.enabled && !this.droppable) return;
			registeredComponents.set(this.$el, this);
		},
		unregisterComponent() {
			//console.log("unregisterComponent: ", this.id);
			if (!this.item && !this.id) return;
			registeredComponents.delete(this.$el);
		},
		/**
		 * enabled/droppable реактивны и могут меняться без пересоздания компонента
		 * (например, включение withDND) - регистрацию нужно синхронизировать динамически.
		 */
		syncRegistration() {
			if (this.enabled || this.droppable) this.registerComponent();
			else this.unregisterComponent();
		},
	},
	watch: {
		enabled: "syncRegistration",
		droppable: "syncRegistration",
	},
	mounted() {
		this.registerComponent();

		/*if (this.id === "dummy-droppable-before-tpjHXazFRJqIsDOT9s74WyFl") {
			console.log("COMPONENT MOUNTED! " + this.id);
		}*/
	},
	beforeDestroy() {
		this.unregisterComponent();
		this.deleteDummy();

		/*if (this.id === "dummy-droppable-before-tpjHXazFRJqIsDOT9s74WyFl") {
			console.log("COMPONENT DESTROYED! " + this.id);
		}*/

	}
}
</script>

<style lang="scss">

$dropping-time: .25s;

.draggable {
	//box-shadow: inset 0 0 .5rem rgba(0, 0, 0, 0.25);
	transition: opacity $dropping-time, background-color $dropping-time;
	//border: 2px dotted blue;

	&:hover {
		.draggable__handle {
			opacity: 1;//$default-icon-opacity;
		}
	}

	&.--draggable {
		user-select: none;
	}

	&.--inline {
		display: inline-block;
	}

	&.--dragging {
		opacity: 0.1;
		pointer-events: none;
	}

	&.--over {
		position: relative;
		//box-shadow: 0 0 15px map-get($blue, "lighten-3");
		//background-color: map-get($blue, "lighten-5");
		//background-color: map-get($grey, "lighten-3");
		//opacity: 0.5;
		&:after {
			content: "";
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			//opacity: 1;
			//border: 1px dotted map-get($blue, "lighten-2");
			border: 1px dotted map-get($blue, "lighten-2");
			//transition-delay:1.5s;
		}
	}

	&__dummy {
		position: fixed;
		z-index: $z-index__dnd;
		box-shadow: 0 0 15px map-get($blue, "lighten-3");
		//background: $white;
		//pointer-events: none;
		cursor: pointer;
		//opacity: 0;
		//filter: blur(1px);

		&.--dragged {
			transition: transform $dropping-time, opacity $dropping-time;
			//transform: rotateZ(-1deg) scale(1.05);
			transform: scale(1.025);
			z-index: $z-index__dnd;
			opacity: .4; //65;

			&-secondary {
				transition: $dropping-time;
				//box-shadow: 0 0 5px map-get($blue, "lighten-3");
				z-index: $z-index__dnd - 1;
				opacity: 0.4; //65;
			}
		}

		&.--dropping {
			transition: $dropping-time;
			transform-origin: 0;
			transform: scale(1);
			//opacity: 0;
		}

		/*&.--dropping-moved {
			transition: all .3s;
		}*/

		&.--returning {
			transition: $dropping-time;
			transform: scale(1);
			//opacity: 0;
		}

		&.--hidden {
			//transition: opacity $dropping-time;
			opacity: 0;
		}

		&.--multiple:not(.--returning):not(.--hidden) {
			&::before {
				opacity: 0.5;
			}
		}

		&::before {
			content: "";
			position: absolute;
			z-index: -1;
			width: 100%;
			height: 100%;
			background: $white;
			box-shadow: 0 0 15px map-get($blue, "lighten-1");
			left: 10px;
			top: 10px;

			transition: opacity $dropping-time;
			opacity: 0;
		}

		/*& * {
			pointer-events: none;
		}*/
	}

	&__handle {
		position: absolute;
		left: 0.25rem;
		top: 50%;
		transform: translateY(-50%);
		cursor: grab;
		z-index: 1;
		/*background-image: linear-gradient(circle, #333 2px, transparent 2px);
		background-size: 6px 6px;
		background-repeat: repeat;*/
		width: 16px;
		height: 24px;

		@include passive-icon();

		/*&::before {
			content: '';
			position: absolute;
			top: 2px;
			left: 2px;
			width: 2px;
			height: 2px;
			border-radius: 50%;
			background: #333;

			!* Duplicate the dot to create a 2x3 grid *!
			box-shadow:
				4px 0 #333,
				0 4px #333, 4px 4px #333,
				0 8px #333, 4px 8px #333,
				0 12px #333, 4px 12px #333;
		}*/

	}
}
</style>